import { CompetitionFilterTypes } from "~pages/Competitions/CompetitionsListPage/enums/CompetitionFilterTypes.ts";
import { CompetitionTableColumns } from "~pages/Competitions/CompetitionsListPage/enums/CompetitionTableColumns.ts";
import { FixturesFilterTypes } from "~pages/Fixtures/FixtureListPage/types";
import { FixtureTableColumns } from "~pages/Fixtures/FixtureListPage/types/fixtureColumnsEnum.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import type { TableColumnGroupDefinition } from "~types/tableColumnGroupDefinition.ts";
import type { UiConfiguration } from "~types/uiConfiguration.ts";

const fixtureTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "fixtures.table.column.chooser.title.details",
    columns: [
      { key: FixtureTableColumns.FixtureId },
      { key: FixtureTableColumns.ExternalId },
      { key: FixtureTableColumns.RoundNumber, defaultInColumnChooser: true },
      { key: FixtureTableColumns.HomeCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Result, defaultInColumnChooser: true },
      { key: FixtureTableColumns.AwayCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureNumber, defaultInColumnChooser: true },
      { key: FixtureTableColumns.NameLocal },
      { key: FixtureTableColumns.NameLatin },
      { key: FixtureTableColumns.VenueId, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartDate, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartTime, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureType, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Status, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Stage, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StageCode },
      { key: FixtureTableColumns.Pool, defaultInColumnChooser: true },
      { key: FixtureTableColumns.PoolCode },
      { key: FixtureTableColumns.Series },
      { key: FixtureTableColumns.SeriesCode },
      { key: FixtureTableColumns.Added },
      { key: FixtureTableColumns.Attendance },
      { key: FixtureTableColumns.Discipline },
      { key: FixtureTableColumns.StartTimeActualUtc },
      { key: FixtureTableColumns.EndTimeActualUtc },
      { key: FixtureTableColumns.FixtureProfile },
      { key: FixtureTableColumns.ProfileId },
      { key: FixtureTableColumns.InternationalReference },
      { key: FixtureTableColumns.Round },
      { key: FixtureTableColumns.RoundCode },
      { key: FixtureTableColumns.SeriesFixtureNumber },
      { key: FixtureTableColumns.TimesUnconfirmed },
      { key: FixtureTableColumns.StartTimeUtc },
    ],
  },
];

const competitionTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "competitions.table.column.chooser.title.details",
    columns: [
      { key: CompetitionTableColumns.CompetitionId },
      { key: CompetitionTableColumns.ExternalId },
      { key: CompetitionTableColumns.Images, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.NameLatin },
      { key: CompetitionTableColumns.AbbreviationLocal, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.AbbreviationLatin },
      { key: CompetitionTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.InternationalReference },
      { key: CompetitionTableColumns.League },
    ],
  },
];

const seasonRosterColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "roster.columns.person.title",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "person.externalId", isDefault: true },
      { key: "person.nameFullLatin" },
      { key: "person.additionalDetails.height" },
      { key: "person.additionalDetails.weight" },
      { key: "person.additionalDetails.heightImperial" },
      { key: "person.additionalDetails.weightImperial" },
      { key: "person.additionalDetails.representation" },
      { key: "person.additionalDetails.juniorAssociationLeague" },
      { key: "person.additionalDetails.homeTown" },
      { key: "person.additionalDetails.college" },
      { key: "person.additionalDetails.collegeClass" },
      { key: "person.additionalDetails.school" },
      { key: "person.additionalDetails.schoolClass" },
      { key: "dob" },
      { key: "person.gender" },
      { key: "person.nationality" },
      { key: "person.representing" },
      { key: "person.status" },
    ],
  },
  {
    titleIntlKey: "roster.columns.roster.title",
    columns: [
      { key: "bib", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "position", isDefault: true },
      { key: "status", isDefault: true },
      { key: "isCaptain", isDefault: true },
    ],
  },
];

const fixtureRosterColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "roster.columns.person.title",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "person.externalId", isDefault: true },
      { key: "person.nameFullLatin" },
      { key: "person.additionalDetails.height" },
      { key: "person.additionalDetails.weight" },
      { key: "person.additionalDetails.heightImperial" },
      { key: "person.additionalDetails.weightImperial" },
      { key: "person.additionalDetails.representation" },
      { key: "person.additionalDetails.juniorAssociationLeague" },
      { key: "person.additionalDetails.homeTown" },
      { key: "person.additionalDetails.college" },
      { key: "person.additionalDetails.collegeClass" },
      { key: "person.additionalDetails.school" },
      { key: "person.additionalDetails.schoolClass" },
      { key: "dob" },
      { key: "person.gender" },
      { key: "person.nationality" },
      { key: "person.representing" },
      { key: "person.status" },
    ],
  },
  {
    titleIntlKey: "roster.columns.roster.title",
    columns: [
      { key: "bib", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "position", isDefault: true },
      { key: "starter", isDefault: true },
      { key: "status", isDefault: true },
      { key: "isCaptain", isDefault: true },
    ],
  },
];

const personsColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "person.columns",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "nameFullLatin" },
      { key: "additionalDetails.height" },
      { key: "additionalDetails.weight" },
      { key: "additionalDetails.heightImperial" },
      { key: "additionalDetails.weightImperial" },
      { key: "additionalDetails.representation" },
      { key: "additionalDetails.juniorAssociationLeague" },
      { key: "additionalDetails.homeTown" },
      { key: "additionalDetails.college" },
      { key: "additionalDetails.collegeClass" },
      { key: "additionalDetails.school" },
      { key: "additionalDetails.schoolClass" },
      { key: "dob", isDefault: true },
      { key: "gender", isDefault: true },
      { key: "nationality", isDefault: true },
      { key: "status", isDefault: true },
    ],
  },
];

const dashboardColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "dashboard.columns",
    columns: [
      { key: "year", isDefault: true },
      { key: "competitionName", isDefault: true },
      { key: "nameLocal", isDefault: true },
      { key: "gender", isDefault: true },
      { key: "ageGroup", isDefault: true },
      { key: "status", isDefault: true },
    ],
  },
];

//return back key config for columns, remove show from filterconfig
export const defaultUiConfig: UiConfiguration = {
  pages: {
    seasonRoster: { tableColumnGroups: seasonRosterColumnGroups },
    fixtureRoster: { tableColumnGroups: fixtureRosterColumnGroups },
    fixtures: {
      listPage: {
        filtersConfig: [
          { filterKey: FixturesFilterTypes.Stages },
          { filterKey: FixturesFilterTypes.RoundNumber },
          { filterKey: FixturesFilterTypes.Pools },
          { filterKey: FixturesFilterTypes.Competitors },
          { filterKey: FixturesFilterTypes.Venues },
          { filterKey: FixturesFilterTypes.FixtureStatuses },
          { filterKey: FixturesFilterTypes.DateRange },
        ],
        columnsConfig: fixtureTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.entities",
          descriptionIntlKey: "import.modal.text.entities",
        },
      },
    },
    competitions: {
      listPage: {
        columnsConfig: competitionTableColumnsArray,
        filtersConfig: [{ filterKey: CompetitionFilterTypes.AgeGroup }, { filterKey: CompetitionFilterTypes.League }],
      },
    },
    persons: { tableColumnGroups: personsColumnGroups },
    dashboard: { tableColumnGroups: dashboardColumnGroups },
    seasonUpsert: {
      promotionRelegationRules: {
        enabled: false,
      },
      disciplines: {
        enabled: false,
        isNullable: true,
      },
    },
    entityStatistic: {
      enabled: false,
    },
  },
};
